import { MessagesType } from '@telialt/common-cms/src/model';
import { SearchFilterValue } from '@telialt/core-search/src/model';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { SUPPORTED_COMMITMENTS } from './constants';
import { Channel, Commitment, OfferGroup, ServiceOffersRequestParams } from './model';

export const getSortSelectOptions = (t: MessagesType) => {
    return [
        {
            label: t['plans.plans_list.sorting.default'],
            apiValue: undefined,
        },
        {
            label: t['plans.plans_list.sorting.price_desc'],
            apiValue: [{ ['price']: 'desc' }],
        },
        {
            label: t['plans.plans_list.sorting.price_asc'],
            apiValue: [{ ['price']: 'asc' }],
        },
        {
            label: t['plans.plans_list.sorting.data_desc'],
            apiValue: [{ ['dataSizeDomesticTotal']: 'desc' }],
        },
        {
            label: t['plans.plans_list.sorting.data_asc'],
            apiValue: [{ ['dataSizeDomesticTotal']: 'asc' }],
        },
    ] as const;
};

export const getCommitmentSelectOptions = (t: MessagesType, offerGroup?: OfferGroup) => {
    const group = ({ MHS: 'mhs', MBB: 'mbb' } as const)[offerGroup];
    const keyBase = 'plans.plans_list.commitment';

    return SUPPORTED_COMMITMENTS.map((commitment) => ({
        label: t[group ? `${keyBase}.${group}.${commitment}` : `${keyBase}.${commitment}`],
        paramKey: t[group ? `${keyBase}.${group}.${commitment}_search_param` : `${keyBase}.${commitment}_search_param`],
        apiValue: commitment,
    }));
};

export const getAvailableCommitments = (
    t: MessagesType,
    availableCommitmentTypes: Commitment[],
    offerGroup?: OfferGroup,
) => {
    return availableCommitmentTypes.map((value) =>
        getCommitmentSelectOptions(t, offerGroup).find((option) => option.apiValue === value),
    );
};

export const getSortingValueByLabel = ({
    label,
    t,
}: {
    label?: string;
    t: MessagesType;
}): Array<Record<string, 'asc' | 'desc'>> | undefined => {
    return getSortSelectOptions(t).find((option) => option.label === label)?.apiValue || undefined;
};

export const getCommitmentValueByParamKey = ({
    paramKey,
    t,
    offerGroup,
}: {
    paramKey?: string;
    t: MessagesType;
    offerGroup: OfferGroup;
}): string | undefined => {
    return (
        getCommitmentSelectOptions(t, offerGroup).find((option) => option.paramKey === paramKey)?.apiValue || undefined
    );
};

type GenerateParams = {
    offerGroup: OfferGroup;
    channel: Channel;
    segment: TeliaSegment;
    sorting?: Array<Record<string, 'asc' | 'desc'>>;
    contractDuration?: string;
    isTeliaOne?: string;
};

export const generateServiceOffersRequestParams = (
    params: GenerateParams,
    searchFilters?: Record<string, SearchFilterValue>,
): ServiceOffersRequestParams => {
    return {
        filters: {
            ...searchFilters,
            offerGroup: [params.offerGroup],
            channel: [params.channel],
            segment: [params.segment.toUpperCase()],
            contractDuration: [params.contractDuration],
            isTeliaOne: params.isTeliaOne ? ['true'] : undefined,
        },
        sort: params.sorting,
    };
};

type GetDynamicQueryParamKeyOptions = {
    possibleKeys: string[];
    searchParams?: { [key: string]: string | undefined };
};

export const getDynamicQueryParamKey = (options: GetDynamicQueryParamKeyOptions): string | undefined => {
    const { possibleKeys, searchParams } = options;

    return possibleKeys.find((key) => searchParams?.[key] === '');
};
